import { Divider, Drawer, Empty, Image, Space, Table, Tag } from 'antd';
import moment from 'moment';
import React from 'react';
import { Column } from '../../../utils/column';

interface InterventionFileProjectInformationProps {
  handleModalInformationClose: () => void;
  modalInformation: boolean;
  selectedRecord: any | null;
}

const formatDuration = (hours: any, minutes: any) => {
  const h = Number(hours ?? 0);
  const m = Number(minutes ?? 0);
  if (!h && !m) return '-';
  return `${h}h ${m}min`;
};

const InterventionFileProjectInformation: React.FC<InterventionFileProjectInformationProps> = ({
  handleModalInformationClose,
  modalInformation,
  selectedRecord,
}) => {
  const company = selectedRecord?.pmContract?.company ?? selectedRecord?.company;
  const workPlace = selectedRecord?.workPlace;
  const installations = selectedRecord?.interventionFileProjectInstallations ?? [];

  const companyDetailsColumns: Column[] = [
    {
      title: 'Firma',
      dataIndex: '',
      key: 'company',
      render: () => <strong>{company?.name ?? '-'}</strong>,
    },
    {
      title: 'Punct de lucru',
      dataIndex: '',
      key: 'workPlaceName',
      render: () => <strong>{workPlace?.name ?? '-'}</strong>,
    },
    {
      title: 'Adresa',
      dataIndex: '',
      key: 'workPlaceAddress',
      render: () => workPlace?.address ?? '-',
      width: 300,
    },
  ];

  const contactPersons: any[] = [
    ...(workPlace?.contactPersons ?? []),
    ...(company?.contactPersons ?? []),
  ];

  const contactColumns: Column[] = [
    {
      title: 'Nume',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Functie',
      dataIndex: 'role',
      key: 'role',
      render: (role: any) => (Array.isArray(role) ? role.join(' / ') : (role ?? '-')),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: any) => (Array.isArray(phone) ? phone.join(', ') : (phone ?? '-')),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: any) => (Array.isArray(email) ? email.join(', ') : (email ?? '-')),
    },
  ];

  const durationColumns: Column[] = [
    {
      title: 'Data interventiei',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (value: any) => (value ? moment(value).format('DD.MM.YYYY') : '-'),
    },
    {
      title: 'Durata interventie',
      dataIndex: '',
      key: 'duration',
      render: (record: any) => formatDuration(record?.hours, record?.minutes),
    },
    {
      title: 'Timp transport',
      dataIndex: '',
      key: 'timeTransport',
      render: (record: any) => formatDuration(record?.timeTransportHours, record?.timeTransportMinutes),
    },
    {
      title: 'Kilometri parcursi',
      dataIndex: 'traveledKilometers',
      key: 'traveledKilometers',
      render: (value: any) => `${value ?? 0} km`,
    },
  ];

  const installationColumns: Column[] = [
    {
      title: 'Dispozitiv',
      dataIndex: '',
      key: 'device',
      render: (record: any) => <strong>{record?.deviceType?.name ?? '-'}</strong>,
    },
    {
      title: 'Etapa',
      dataIndex: 'stage',
      key: 'stage',
      render: (value: any) => value ?? '-',
    },
    {
      title: 'Tehnicieni',
      dataIndex: 'interventionFileProjectInstallationTehnicians',
      key: 'technicians',
      render: (technicians: any[]) =>
        technicians?.length > 0 ? (
          <Space wrap>
            {technicians.map((tech: any, index: number) => (
              <Tag key={tech?.id ?? index}>{tech?.user?.name ?? '-'}</Tag>
            ))}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: 'Cantitate',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (value: any) => <Tag color='blue'>{value ?? 0}</Tag>,
    },
  ];

  return (
    <Drawer
      title='Informatii fisa de instalare'
      placement='right'
      onClose={handleModalInformationClose}
      open={modalInformation}
      width='50%'
      key='intervention-file-project-drawer'
    >
      {selectedRecord ? (
        <>
          <h2 style={{ textAlign: 'center' }}>
            FISA DE INSTALARE <br />
            Proces verbal de predare-primire Nr. Fisa/P.V: {selectedRecord?.number ?? '-'} <br />
            DATA: {selectedRecord?.deliveryDate ? moment(selectedRecord.deliveryDate).format('DD.MM.YYYY') : '-'}
          </h2>
          <Divider />

          <Table
            bordered
            pagination={false}
            columns={companyDetailsColumns}
            dataSource={[selectedRecord]}
            scroll={{ x: true }}
            rowKey='id'
          />
          <Divider />

          <Divider orientation='left'>Persoane de contact</Divider>
          <Table
            bordered
            pagination={false}
            columns={contactColumns}
            dataSource={contactPersons}
            scroll={{ x: true }}
            rowKey={(record: any) => record?.id ?? record?.name}
            locale={{ emptyText: 'Nu exista persoane de contact' }}
          />
          <Divider />

          <Divider orientation='left'>Durata interventiei</Divider>
          <Table
            bordered
            pagination={false}
            columns={durationColumns}
            dataSource={[selectedRecord]}
            scroll={{ x: true }}
            rowKey='id'
          />
          <Divider />

          <Divider orientation='left'>Instalari</Divider>
          <Table
            bordered
            pagination={false}
            columns={installationColumns}
            dataSource={installations}
            scroll={{ x: true }}
            rowKey={(record: any) => record?.id ?? `${record?.deviceTypeId}-${record?.stage}`}
            locale={{ emptyText: 'Nu exista instalari' }}
          />
          <Divider />

          <Divider orientation='left'>Observatii</Divider>
          <p style={{ whiteSpace: 'pre-wrap' }}>{selectedRecord?.observations || '-'}</p>

          {selectedRecord?.attachedImage && (
            <>
              <Divider orientation='left'>Imagine atasata</Divider>
              <Image
                width={250}
                src={`data:image/png;base64,${selectedRecord.attachedImage}`}
                alt='Imagine atasata'
              />
            </>
          )}
        </>
      ) : (
        <Empty description='Nu exista date de afisat' />
      )}
    </Drawer>
  );
};

export default InterventionFileProjectInformation;
